import {createSvgElement} from "../lib/domFunctions";
import {ContainerView} from "./Containers";
import {PositionView} from "./Positions";
import {TransferView, TransportView} from "./Transports";
import {createContainerTrackGenerator} from "./ContainerTrackGenerator";
import {ContainerPathTrack, ContainerTrackGenerator, ContainerTrackLine} from "./ContainerTrackApi";

const ColorCount = 4;

export const ContainerPathTrackCustomStyles: { [key: string]: string } = {
	'svg-c-track-bracket': 'fill:none;stroke:#a5a0a0',
	'svg-c-track': 'stroke-width: 1;stroke-linecap:round;stroke-linejoin:miter;stroke-miterlimit:4;stroke-dasharray: 6,3;stroke-opacity: 1',
	'svg-c-track-0': 'fill:#77e36a;stroke:#77e36a',
	'svg-c-track-0-end': 'marker-end:url(#CTCircle0)',
	'svg-c-track-0-opusk': 'marker-end:url(#CTCircle0FillWhite)',
	'svg-c-track-1': 'fill:#6a95e3;stroke:#6a95e3',
	'svg-c-track-1-end': 'marker-end:url(#CTCircle1)',
	'svg-c-track-1-opusk': 'marker-end:url(#CTCircle1FillWhite)',
	'svg-c-track-2': 'fill:#b553c7;stroke:#b553c7',
	'svg-c-track-2-end': 'marker-end:url(#CTCircle2)',
	'svg-c-track-2-opusk': 'marker-end:url(#CTCircle2FillWhite)',
	'svg-c-track-3': 'fill:#f9ca32;stroke:#f9ca32',
	'svg-c-track-3-end': 'marker-end:url(#CTCircle3)',
	'svg-c-track-3-opusk': 'marker-end:url(#CTCircle3FillWhite)',
	'svg-c-track-sw': 'marker-start:url(#CTWave)',
	'svg-c-track-ew': 'marker-end:url(#CTWave)',
}

export function ContainerPathTrackCustomDefs() {
	const coloredMarkers = Array.from(Array(ColorCount).keys())
		.flatMap(k => {
			const circle = createSvgElement('marker', {id: `CTCircle${k}`, markerWidth: "10", markerHeight: "10", viewBox: "0,0,10,10", refY: "5", refX: "5"});
			circle.append(createSvgElement('circle', {cx: "5", cy: "5", r: "3", class: `svg-c-track-${k}`, style: 'fill-rule:evenodd'}));
			const opusk = createSvgElement('marker', {id: `CTCircle${k}FillWhite`, markerWidth: "10", markerHeight: "10", viewBox: "0,0,10,10", refY: "5", refX: "5"});
			opusk.append(createSvgElement('circle', {cx: "5", cy: "5", r: "3", class: `svg-c-track-${k}`, style: 'fill-rule:evenodd;fill: white'}));
			return [circle, opusk];
		})

	const CTWave = createSvgElement('marker', {id: 'CTWave', markerWidth: "10", markerHeight: "10", viewBox: "0,0,10,10", refY: "5", refX: "5"});
	CTWave.append(createSvgElement('path', {d: "M 5,0 C 0,5 10,5 5,10", class: 'svg-c-track-bracket'}))
	return coloredMarkers.concat(CTWave);
}

export interface SchemaContainerTracksPlayerAdapter {
	readonly root: SVGElement;
	readonly mirrorSchema: boolean;

	getContainerView(id: number): ContainerView | null;

	getPositionView(position: number): PositionView;

	getAllPositions(): PositionView[];

	getTransportView(transport: number): TransportView;

	findTransferByMove(startPosition: number, endPosition: number): TransferView | null;

	findTransferByPosition(startOrEndPosition: number): TransferView | null;

	getAllPositionLineY(): number[];
}


export class SchemaContainerTracks {
	private readonly player: SchemaContainerTracksPlayerAdapter;
	private readonly generator: ContainerTrackGenerator;

	private readonly containerTracks = new Map<number, ContainerPathTrack>()

	private colorSeed = 0;
	private colorAssigned = new Map<number, number>();

	constructor(player: SchemaContainerTracksPlayerAdapter) {
		this.player = player;
		this.generator = createContainerTrackGenerator(this.player);
	}

	private getColor(containerId: number): number {
		let color = this.colorAssigned.get(containerId);
		if (color == undefined) {
			color = this.colorSeed % ColorCount;
			this.colorSeed += 1;
			this.colorAssigned.set(containerId, color);
		}
		return color;
	}

	public getContainerTrack(containerId: number) {
		return this.containerTracks.get(containerId) || null;
	}

	public setContainerTrack(containerId: number, path: number[] | null) {
		let track = this.containerTracks.get(containerId) || null;
		if (path == null && track == null) {
			return false;
		}

		// рисуем трек только до первой реальной позиции (не трансфер)
		if (path != null) {
			let trackEndIndex = path.findIndex((position) => {
				const transfer = this.player.findTransferByPosition(position)
				if (transfer != null) return false;
				return this.player.getPositionView(position) != null;
			});

			if (trackEndIndex != -1) {
				path = path.slice(0, trackEndIndex + 1);
			}

			if (path.length == 0) {
				path = null;
			}
		}

		// нет пути - убираем трек
		if (path == null) {
			if (track != null) track.clear();
			this.containerTracks.delete(containerId);
			this.colorAssigned.delete(containerId);
			return false;
		}


		if (track == null) {
			track = new ContainerPathTrack(containerId, path);
			this.containerTracks.set(containerId, track);
		}

		// Значит трек все-таки есть. Очищаем текущий и назначаем новый
		track.path = path;
		return this.renderContainerTrack(containerId)
	}

	public removeContainerTrack(containerId: number) {
		const track = this.containerTracks.get(containerId) || null;
		if (!track) return;

		track.clear();
		this.containerTracks.delete(containerId);
		this.colorAssigned.delete(containerId);
	}

	private renderContainerTrack(id: number) {
		const track = this.containerTracks.get(id) || null;
		if (!track) return;
		if (track.path.length == 0) {
			track.clear();
			return false;
		}

		let trackLines: ContainerTrackLine[] = [];
		try {
			trackLines = this.generator.makeTrackLines(track);
			this.generator.avoidIntersections(this.containerTracks, id, trackLines);
		} catch (e) {
			console.error(e);
		}

		// теперь делаем SVG из того, что получилось
		track.reset(trackLines, this.player.root, this.getColor(id));

		return !!trackLines;
	}
}